import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from './client';
import { useRsMutation } from './restaurant';

export type StockUnit = 'GRAM' | 'MILLILITRE' | 'PIECE';
/** NEW: an item added but not counted yet (it is not "out of stock", nobody has counted it). */
export type StockLevel = 'OK' | 'LOW' | 'OUT' | 'NEW';
export type StockCategory = 'FOOD_DRINK' | 'LINEN' | 'GLASS_CROCKERY' | 'KITCHEN_BAR_WARE' | 'ROOM_SUPPLIES' | 'CLEANING' | 'MAINTENANCE' | 'OFFICE' | 'OTHER';

export const CATEGORIES: StockCategory[] = ['FOOD_DRINK', 'LINEN', 'GLASS_CROCKERY', 'KITCHEN_BAR_WARE', 'ROOM_SUPPLIES', 'CLEANING', 'MAINTENANCE', 'OFFICE', 'OTHER'];
export const CATEGORY_LABEL: Record<StockCategory, string> = {
  FOOD_DRINK: 'Food and drink',
  LINEN: 'Linen and bedding',
  GLASS_CROCKERY: 'Glassware and crockery',
  KITCHEN_BAR_WARE: 'Kitchen and bar ware',
  ROOM_SUPPLIES: 'Room supplies',
  CLEANING: 'Cleaning',
  MAINTENANCE: 'Maintenance',
  OFFICE: 'Office',
  OTHER: 'Other',
};
/** What each kind is for, shown when adding an item. */
export const CATEGORY_HINT: Record<StockCategory, string> = {
  FOOD_DRINK: 'Ingredients and drinks the kitchen and bar use up. Only these can be used in recipes, so orders take them off the shelf.',
  LINEN: 'Bed sheets, pillows, towels, curtains, table cloths and napkins.',
  GLASS_CROCKERY: 'Glasses, cups, plates, bowls and cutlery.',
  KITCHEN_BAR_WARE: 'Pots, pans, knives, trays and bar tools.',
  ROOM_SUPPLIES: 'Soap, toilet rolls, slippers, hangers and bottled water for rooms.',
  CLEANING: 'Cleaning products and tools.',
  MAINTENANCE: 'Bulbs, batteries and spare parts.',
  OFFICE: 'Paper, pens and registers.',
  OTHER: 'Anything else the hotel keeps count of.',
};

export interface Ingredient {
  id: string;
  name: string;
  unit: StockUnit;
  /** what one is called when it is not a plain piece: "sets", "pairs", "boxes" */
  unitName: string | null;
  category: StockCategory;
  location: string | null;
  onHand: number;
  reorderLevel: number;
  active: boolean;
  counted: boolean;
  level: StockLevel;
  usedIn: number;
}

export interface StockMovement {
  id: string;
  delta: number;
  balanceAfter: number;
  reason: 'RECEIPT' | 'SALE' | 'RETURN' | 'WASTE' | 'ADJUSTMENT' | 'USE';
  note: string | null;
  by: string;
  at: string;
}

export interface RecipeLine {
  ingredientId: string;
  name: string;
  unit: StockUnit;
  quantity: number;
}

/** One line of the buy list: something that is out or at its reorder level. */
export interface ReorderItem {
  id: string;
  name: string;
  category: StockCategory;
  location: string | null;
  unit: StockUnit;
  unitName: string | null;
  onHand: number;
  reorderLevel: number;
  state: 'OUT' | 'LOW';
  /** the average of the last three deliveries, when there were any */
  usualDelivery: number | null;
  lastDelivered: string | null;
  /** how much to ask for; null when there is nothing to base a quantity on */
  suggested: number | null;
}

export const UNIT_LABEL: Record<StockUnit, string> = { GRAM: 'g', MILLILITRE: 'ml', PIECE: 'pcs' };
/** The word after a number: the item's own ("sets") if it has one, otherwise g, ml or pcs. */
export const unitWord = (unit: StockUnit, unitName?: string | null): string => unitName || UNIT_LABEL[unit];
export const formatQty = (n: number, unit: StockUnit, unitName?: string | null): string => `${n.toLocaleString('en-GH')} ${unitWord(unit, unitName)}`;

export const useIngredients = () => useQuery({ queryKey: ['stock', 'ingredients'], queryFn: () => api<{ ingredients: Ingredient[] }>('/stock/ingredients').then((r) => r.ingredients) });
export const useReorderList = (enabled = true) =>
  useQuery({ queryKey: ['stock', 'reorder'], enabled, queryFn: () => api<{ items: ReorderItem[]; notCounted: number }>('/stock/reorder-list') });
export const useMovements = (id: string | null) => useQuery({ queryKey: ['stock', 'movements', id], enabled: !!id, queryFn: () => api<{ movements: StockMovement[] }>(`/stock/ingredients/${id}/movements`).then((r) => r.movements) });
export const useRecipe = (variantId: string) => useQuery({ queryKey: ['stock', 'recipe', variantId], enabled: !!variantId, queryFn: () => api<{ lines: RecipeLine[] }>(`/menu/variants/${variantId}/recipe`).then((r) => r.lines) });

/** A stock change refreshes the item list, the buy list and the numbers behind them, so no screen shows a stale count. */
export const useStockMutation = <V, R = unknown>(run: (v: V) => Promise<R>) => {
  const qc = useQueryClient();
  return useRsMutation(async (v: V) => {
    const r = await run(v);
    await qc.invalidateQueries({ queryKey: ['stock'] });
    return r;
  });
};
